import PropTypes from "prop-types";
import { cn } from "@helpers/general";
import { useCurrentCurrencySymbol } from "@hooks/usePrices";
import useDeviceDetect from "@hooks/useDeviceDetect";
import useDiscountsInfo, { DISCOUNT_INFO_MODES } from "@hooks/useDiscountsInfo";

import DISCOUNT_TYPES from "@constants/DiscountTypes";
import COLORS from "../../constants/Colors";
import { ComponentType } from "../../constants/ComponentType";

function GiftOfferProgressBar({ saleColors, eligibleAmount, componentType }) {
  const currencySymbol = useCurrentCurrencySymbol();

  const discountInfo = useDiscountsInfo(DISCOUNT_INFO_MODES.CURRENT);
  const giftInfo = discountInfo.find(({ type }) => type === DISCOUNT_TYPES.GIFT_WITH_PURCHASE);

  const milestones = [
    { threshold: giftInfo?.freeShippingThreshold, label: "Free Shipping" },
    { threshold: giftInfo?.minimumPurchaseAmount, label: "Free Gift" },
  ];

  const { isMobile } = useDeviceDetect();

  const backgroundColorReached = saleColors?.tierBgColorLuxeReached ?? COLORS.POPPY;
  const borderColor = saleColors?.tierBorderColorLuxe ?? COLORS.BLACK;
  const textColor = saleColors?.tierTextColorLuxe ?? COLORS.BLACK;
  const textColorReached = saleColors?.tierTextColorLuxeReached ?? COLORS.WHITE;

  const progress = milestones?.map(({ threshold, label }) => {
    const isFilled = eligibleAmount >= threshold;

    return {
      threshold,
      isFilled,
      mainContent: `Spend ${currencySymbol}${threshold}`,
      content: label,
    };
  });

  let showProgressText = false;

  if (
    componentType === ComponentType.SIDE_CART ||
    (componentType === ComponentType.ANNOUNCEMENT_BAR && isMobile)
  ) {
    showProgressText = true;
  }

  return (
    <div
      className={cn(componentType === ComponentType.PDP && "pb-[15px] desktop:pb-[25px]")}
      style={{ color: textColor }}
    >
      {showProgressText && (
        <p className="typo-micro font-bold mb-[10px]">
          Get a Free Gift When You Spend {currencySymbol}
          {giftInfo?.minimumPurchaseAmount}
        </p>
      )}
      <div className="gap-[5px] desktop:gap-[10px]" style={{ display: "flex", width: "100%" }}>
        {progress?.map((item, index) => (
          <div key={`progress-tiered-gift-${index}`} className="flex-1 flex-wrap  min-w-0">
            <div
              className="flex flex-col items-center relative h-[7px] rounded-[8px] mb-[5px] duration-300 transition [transition-property:background-color,color,width] [transition-timing-function:ease]"
              style={{
                backgroundColor: item.isFilled ? backgroundColorReached : "",
                border: `1px solid ${borderColor}`,
              }}
              data-testid={`progress-bar-${index}`}
            >
              &nbsp;
            </div>
            <div
              className={cn(
                "leading-[0]",
                componentType === ComponentType.ANNOUNCEMENT_BAR && !isMobile
                  ? "text-center"
                  : "text-left"
              )}
              style={{
                color: item.isFilled ? textColor : textColorReached,
              }}
              data-testid={`progress-text-${index}`}
            >
              <div className="typo-micro whitespace-break-spaces">
                <span className="whitespace-nowrap">{item.mainContent}&nbsp;/&nbsp;</span>
                <span className="font-bold">{item.content}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

GiftOfferProgressBar.propTypes = {
  saleColors: PropTypes.shape({
    tierBgColorLuxeReached: PropTypes.string,
    tierBorderColorLuxe: PropTypes.string,
    tierTextColorLuxe: PropTypes.string,
    tierTextColorLuxeReached: PropTypes.string,
  }),
  eligibleAmount: PropTypes.number.isRequired,
  componentType: PropTypes.string.isRequired,
};

export default GiftOfferProgressBar;
